// Command channel to timer windows, and the ownership heartbeat.
// Desktop 1.0.13 timer bar (James, 2026-10-01); one-click timers
// (docs/specs/timers-one-click-spec.md, 2026-10-14).
//
// With one-click timers the SLOT is the clock: a new-bundle window pauses or
// resumes a v2 timer by writing the slot directly (lib/timerStore.js).  The
// command channel remains for the windows that keep the clock in React refs —
// old-bundle pop-outs (desktop 1.0.13/14 still on the pre-update page) and the
// old floating bar until the app restarts — and for 'focus'.  A sender writes
// ONE key,
//
//   'lextrack_timer_command' = { id, storageKey, action, at }
//
// which fires a `storage` event in every OTHER window of the app; the writing
// window gets the same command as a same-window COMMAND_EVENT, so a target in
// this very window (Layout's legacy 'focus' listener, an in-page form) hears it
// too.  Each listener applies it only when storageKey is its own slot, the id
// is new to it (so a command heard both ways is delivered once), and it is
// under COMMAND_MAX_AGE_MS old.
//
//   action 'pause' | 'resume'  — applied at cmd.at by the window that holds it
//   action 'focus'             — the window raises itself
//
// COMMAND_ACTIONS is FROZEN: old windows drop unknown actions silently, so a
// new action would look like a window that is not listening.
//
// Plus the ownership heartbeat (useTimerSlotHeartbeat): a window bound to a
// slot stamps 'lextrack_timer_owner:<slot>' = { at, pk, ack, v:2, by, tab }
// every few seconds.  It is a PRESENCE record, not a clock owner: it lets a
// surface focus the existing form instead of opening a second one, answers a
// sender's ack wait (`ack` = the id of the last command the window HEARD,
// written the moment it hears one), and shows "open in another window".  A
// record WITHOUT v is an old-bundle window (lib/timerStore.js classify).
//
// Both hooks are on by default on every platform; they no longer depend on
// window.electronAPI.capabilities.timerBar (pass enabled:false to switch one
// off).

import { useEffect, useRef } from 'react'
import { isTimerSlotKey, ownerKeyFor, OWNER_HEARTBEAT_MS } from './timerSlots'

export const COMMAND_KEY = 'lextrack_timer_command'
// Same-window twin of the storage event (a window never gets a storage event
// for its own write).  detail = the command object.
export const COMMAND_EVENT = 'lextrack-timer-command'
export const COMMAND_MAX_AGE_MS = 10000
export const COMMAND_ACTIONS = Object.freeze(['pause', 'resume', 'focus'])
// Same-machine clocks, but allow a little skew rather than drop a fresh click.
const FUTURE_SKEW_MS = 1000
const SEEN_CAP = 200

const newId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

// This window's id, written into its owner records as `tab` so a surface can
// tell "held by me" from "open in another window or tab".  One per JS realm
// (= per window / tab), fixed for the page's life.
const TAB_ID = `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
export const getTabId = () => TAB_ID

const windowStorage = () => {
  try { return typeof window !== 'undefined' ? window.localStorage : null } catch { return null }
}

export function makeCommand ({ storageKey, action, now = Date.now(), id = newId() }) {
  if (!isTimerSlotKey(storageKey) || !COMMAND_ACTIONS.includes(action)) return null
  return { id: String(id), storageKey, action, at: now }
}

// Write a command.  Returns the command written, or null (bad input / storage
// unavailable).  The fresh id makes every value distinct, which matters: the
// browser suppresses the storage event when a key is set to its current value.
// When the command goes through this window's own localStorage, the same
// command is also dispatched as COMMAND_EVENT so a listener in THIS window
// hears it (pass dispatch:false to skip).
export function sendTimerCommand (storageKey, action, { storage, now, dispatch = true } = {}) {
  const cmd = makeCommand({ storageKey, action, now: now ?? Date.now() })
  if (!cmd) return null
  const own = windowStorage()
  const s = storage || own
  try {
    if (!s) return null
    s.setItem(COMMAND_KEY, JSON.stringify(cmd))
  } catch {
    return null
  }
  if (dispatch && s === own && typeof window !== 'undefined') {
    try { window.dispatchEvent(new CustomEvent(COMMAND_EVENT, { detail: cmd })) } catch { /* no CustomEvent */ }
  }
  return cmd
}

export function parseCommand (raw) {
  if (typeof raw !== 'string' || !raw) return null
  try {
    const c = JSON.parse(raw)
    if (!c || typeof c !== 'object') return null
    if (typeof c.id !== 'string' || !c.id) return null
    if (!isTimerSlotKey(c.storageKey)) return null
    if (!COMMAND_ACTIONS.includes(c.action)) return null
    const at = Number(c.at)
    if (!Number.isFinite(at)) return null
    return { id: c.id, storageKey: c.storageKey, action: c.action, at }
  } catch {
    return null
  }
}

// The three conditions from the contract, in one place: my slot, an id I have
// not applied, and fresh (a window opened later must not replay an old click).
export function shouldApplyCommand (cmd, { storageKey, seen, now }) {
  if (!cmd || cmd.storageKey !== storageKey) return false
  if (seen && seen.has(cmd.id)) return false
  const age = now - cmd.at
  return age < COMMAND_MAX_AGE_MS && age >= -FUTURE_SKEW_MS
}

export function rememberCommand (seen, id) {
  seen.add(id)
  if (seen.size > SEEN_CAP) {
    const oldest = seen.values().next().value
    seen.delete(oldest)
  }
}

// Call onCommand(cmd) for each command addressed to `storageKey`, from
// another window (storage event) or from this one (COMMAND_EVENT) — once per
// id however it arrives.  Never reads the key on mount, so a command is
// applied at most once and only by windows that were open for it.
export function useTimerCommandListener (storageKey, onCommand, { enabled = true } = {}) {
  const cbRef = useRef(onCommand)
  useEffect(() => { cbRef.current = onCommand })
  useEffect(() => {
    if (!enabled || !storageKey || typeof window === 'undefined') return undefined
    const seen = new Set()
    const deliver = (cmd) => {
      if (!shouldApplyCommand(cmd, { storageKey, seen, now: Date.now() })) return
      rememberCommand(seen, cmd.id)
      try { if (typeof cbRef.current === 'function') cbRef.current(cmd) } catch { /* a bad command must never break the timer */ }
    }
    const onStorage = (e) => {
      if (!e || e.key !== COMMAND_KEY || !e.newValue) return
      deliver(parseCommand(e.newValue))
    }
    const onLocal = (e) => {
      const d = e && e.detail
      if (!d || typeof d !== 'object') return
      let raw = null
      try { raw = JSON.stringify(d) } catch { return }
      deliver(parseCommand(raw))
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener(COMMAND_EVENT, onLocal)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(COMMAND_EVENT, onLocal)
    }
  }, [enabled, storageKey])
}

// Every new-bundle window bound to a slot (pop-out or in-page form): keep
// 'lextrack_timer_owner:<slot>' fresh while the slot exists, and release it
// the moment the window goes away.  Only written while the slot exists, so an
// idle window (or one that has just saved) adds no storage traffic.  `active`
// re-stamps at once when it changes (a timer just started), instead of up to
// OWNER_HEARTBEAT_MS later — otherwise a quick click in that gap would look
// like an orphan and open a second window on the same slot.  `by` is
// 'popout' or 'inpage' (defaults from whether a popupKey was given).
//
// Returns a stable { ack(id) }: record that this window heard command `id`
// and stamp the record at once (the sender is waiting for it).
export function useTimerSlotHeartbeat (storageKey, { enabled = true, popupKey = null, active = false, by = null } = {}) {
  const ackRef = useRef(null)
  const beatRef = useRef(() => {})
  const apiRef = useRef(null)
  if (!apiRef.current) {
    apiRef.current = {
      ack: (id) => {
        if (typeof id === 'string' && id) ackRef.current = id
        beatRef.current()
      }
    }
  }
  useEffect(() => {
    if (!enabled || !storageKey || typeof window === 'undefined') return undefined
    const ownerKey = ownerKeyFor(storageKey)
    const role = by === 'popout' || by === 'inpage' ? by : (popupKey ? 'popout' : 'inpage')
    const beat = () => {
      try {
        if (window.localStorage.getItem(storageKey)) {
          window.localStorage.setItem(ownerKey, JSON.stringify({
            at: Date.now(), pk: popupKey || null, ack: ackRef.current, v: 2, by: role, tab: TAB_ID
          }))
        } else {
          window.localStorage.removeItem(ownerKey)
        }
      } catch { /* storage unavailable — other windows just treat the timer as unowned */ }
    }
    const release = () => { try { window.localStorage.removeItem(ownerKey) } catch {} }
    beatRef.current = beat
    beat()
    const t = setInterval(beat, OWNER_HEARTBEAT_MS)
    // pagehide + beforeunload both fire when a window closes; either releases
    // at once so other surfaces offer "reopen" immediately rather than after
    // the freshness window lapses.
    window.addEventListener('pagehide', release)
    window.addEventListener('beforeunload', release)
    return () => {
      beatRef.current = () => {}
      clearInterval(t)
      window.removeEventListener('pagehide', release)
      window.removeEventListener('beforeunload', release)
      release()
    }
  }, [enabled, storageKey, popupKey, active, by])
  return apiRef.current
}
